import { createSlice, current, createEntityAdapter, createSelector } from '@reduxjs/toolkit';
import {
    firstLoad, getAddress, getKilometersOneAsset,
    changeOdometer, changeIcon, shareLink
} from './assetsApi';
import moment from 'moment';

/**
 * MIGRATION STATUS: In-Progress
 * 
 * We are in the process of migrating from the legacy rack-based state structure
 * to a normalized Redux state using entity adapters. This file contains both 
 * the new entity-based implementation and the commented-out legacy code.
 * 
 * Once we verify that no components still depend on the rack structure, we can
 * remove all comments marked with "Legacy" and clean up the codebase.
 * 
 * Migrated:
 * - All component selectors now use entity adapter selectors
 * - All reducers use adapter methods for state updates
 * - Summaries and utilities use normalized state instead of rack
 * 
 * IMPORTANT: Run the application to identify any lingering dependencies before
 * removing the commented code.
 */

let summaryTime = new Date().getTime();

// Create entity adapter for vehicles
const vehiclesAdapter = createEntityAdapter({
    selectId: (vehicle) => vehicle.vehicleDevice.assetId,
    // Optional: Add sorting if needed
    sortComparer: (a, b) => a.vehicleDevice.regn.localeCompare(b.vehicleDevice.regn)
});

// Initial state with both entity adapter state and legacy state
const initialState = vehiclesAdapter.getInitialState({
    isLoading: false,
    loaded: false,
    socketLive: false,
    assetList: [],
    rack: {}, // Empty rack for backward compatibility - will be removed
    assetNames: {},
    pinnedAssets: [],
    visibleAssetsList: [],
    visibleAssetsMarkers: [],
    detailView: null,
    fleetSummary: {},
    hasGensetVehicles: false,
    hasFuelVehicles: false,
    viewType: 'fullList',
});

export const assetSocketConnect = payload => ({ type: 'assetSocketConnect', payload });
export const assetSocketConnecting = payload => ({ type: 'assetSocketConnecting', payload });
export const assetSocketConnected = payload => ({ type: 'assetSocketConnected', payload });
export const assetSocketDisconnect = payload => ({ type: 'assetSocketDisconnect', payload });
export const assetSocketDisconnected = payload => ({ type: 'assetSocketDisconnected', payload });
export const assetBatchRun = () => ({ type: 'assetBatchRun' });

export const assetsSlice = createSlice({
    name: 'assets',
    initialState: initialState,
    reducers: {
        assetCombineData: (state, { payload }) => {
            if (state.loaded) {
                // Handle batch updates
                const updates = Array.isArray(payload) ? payload : [payload];

                // Process each update
                updates.forEach(update => {
                    const assetID = `${update.assetID}`;
                    if (current(state.assetList).indexOf(assetID) !== -1) {

                        // Get the current vehicle from state
                        const existingVehicle = adapterSelectors.selectById(state, assetID);

                        if (existingVehicle) {
                            // Prepare updated vehicle data
                            const coreData = { ...existingVehicle.coreData, ...update.coreData };
                            const updatedStatusMeta = statusMeta(coreData);

                            // Get current tail or initialize if needed
                            const currentTail = existingVehicle.tail || [];
                            const newTail = [...currentTail];

                            // Update tail
                            if (newTail.length > 30) {
                                newTail.pop();
                            }
                            newTail.unshift({
                                dt_tracker: update.coreData.dt_tracker,
                                lat: update.coreData.lat,
                                lng: update.coreData.lng,
                                speed: update.coreData.speed,
                            });
                            newTail.sort((a, b) => moment(b.dt_tracker).unix() - moment(a.dt_tracker).unix());

                            // Format sensor data
                            const newSensorData = sensorFormatter(update.sensorData, existingVehicle.sensorData);
                            if (!state.hasGensetVehicles && hasSensorMatch(newSensorData, ['generator', 'genset', 'gen'])) {
                                state.hasGensetVehicles = true;
                            }
                            if (!state.hasFuelVehicles && hasSensorMatch(newSensorData, ['fuel', 'diesel'])) {
                                state.hasFuelVehicles = true;
                            }

                            // Update vehicle in entity adapter
                            const updatedVehicle = {
                                ...existingVehicle,
                                coreData,
                                statusMeta: updatedStatusMeta,
                                tail: newTail,
                                sensorData: newSensorData
                            };

                            // Update entity in state
                            vehiclesAdapter.updateOne(state, {
                                id: assetID,
                                changes: updatedVehicle
                            });

                            // Legacy state updates (temporary during migration)
                            // state.rack[`${assetID}`] = updatedVehicle;
                        }
                    }
                });

                // Update summaries after processing all updates
                const currTime = new Date().getTime();
                if (currTime - summaryTime > 60000) {
                    // Update summaries using the entity adapter
                    const allVehicles = adapterSelectors.selectAll(state);
                    state.fleetSummary = summaryMakerAdapter(allVehicles);

                    // Legacy approach
                    // state.fleetSummary = summaryMaker(state.rack);

                    summaryTime = new Date().getTime();
                }

                // Legacy approach - will be removed after migration is complete
                // state.gensetSummary = gensetSummary(state.rack);
            }
            return state;
        },
        assetCoreData: (state, { payload }) => {
            //handle case where payload coredata doesnt contain all 12 necessary keys
            if (state.loaded) {
                const assetID = `${payload.assetID}`;
                if (current(state.assetList).indexOf(assetID) !== -1) {
                    // Get the current vehicle from state
                    const existingVehicle = adapterSelectors.selectById(state, assetID);

                    if (existingVehicle) {
                        // Update core data
                        const coreData = { ...existingVehicle.coreData, ...payload.coreData };
                        const updatedStatusMeta = statusMeta(coreData);

                        // Get current tail or initialize if needed
                        const currentTail = existingVehicle.tail || [];
                        const newTail = [...currentTail];

                        // Update tail
                        if (newTail.length > 30) {
                            newTail.pop();
                        }
                        newTail.unshift({
                            dt_tracker: payload.coreData.dt_tracker,
                            lat: payload.coreData.lat,
                            lng: payload.coreData.lng,
                            speed: payload.coreData.speed,
                        });

                        // Update vehicle in entity adapter
                        const updatedVehicle = {
                            ...existingVehicle,
                            coreData,
                            statusMeta: updatedStatusMeta,
                            tail: newTail
                        };

                        // Update entity in state
                        vehiclesAdapter.updateOne(state, {
                            id: assetID,
                            changes: updatedVehicle
                        });

                        // Legacy state updates (temporary during migration)
                        // state.rack[`${assetID}`].coreData = coreData;
                        // state.rack[`${assetID}`].statusMeta = updatedStatusMeta;
                        // state.rack[`${assetID}`].tail = newTail;

                        // Update summaries after processing all updates
                        const currTime = new Date().getTime();
                        if (currTime - summaryTime > 60000) {
                            // Update summaries using the entity adapter
                            const allVehicles = adapterSelectors.selectAll(state);
                            state.fleetSummary = summaryMakerAdapter(allVehicles);

                            // Legacy approach
                            // state.fleetSummary = summaryMaker(state.rack);

                            summaryTime = new Date().getTime();
                        }
                    }
                    return state;
                } else {
                    //console.log("Asset not in our rack -coreData", payload.assetID);
                }
            }
            //console.log("Coredata reducer");
            return state;
        },
        assetSensorData: (state, { payload }) => {
            if (state.loaded) {
                const assetID = `${payload.assetID}`;
                if (assetID === '350612075060903') {
                    console.log("Asset Sensor Data", payload);
                }
                if (current(state.assetList).indexOf(assetID) !== -1) {
                    // Get the current vehicle from state
                    const existingVehicle = adapterSelectors.selectById(state, assetID);

                    if (existingVehicle) {
                        // Format sensor data
                        const newSensorData = sensorFormatter(payload.sensors, existingVehicle.sensorData);

                        const hasGenset = hasSensorMatch(newSensorData, ['generator', 'genset', 'gen']);
                        const hasFuel = hasSensorMatch(newSensorData, ['fuel', 'diesel']);
                        if (hasGenset) state.hasGensetVehicles = true;
                        if (hasFuel) state.hasFuelVehicles = true;

                        // Update vehicle in entity adapter
                        vehiclesAdapter.updateOne(state, {
                            id: assetID,
                            changes: {
                                sensorData: newSensorData
                            }
                        });

                        // Legacy state updates (temporary during migration)
                        // state.rack[`${assetID}`].sensorData = newSensorData;
                    }
                } else {
                    //console.log("Asset not in our rack -sensor", payload.assetID);
                }
            }
            return state;
        },
        socketIsLive: (state) => {
            state.socketLive = true;
            return state;
        },
        socketIsDead: (state) => {
            state.socketLive = false;
            return state;
        },
        setDetailView: (state, { payload }) => {
            state.detailView = payload;
            console.log("Set Detail view", payload)
            if (state.visibleAssetsMarkers.indexOf(payload) === -1) {
                state.visibleAssetsMarkers.push(payload);
            }
            return state;
        },
        removeDetailView: (state) => {
            state.detailView = null;
            return state;
        },
        addToPinnedAsset: (state, { payload }) => {
            //console.log("Pinning asset", payload);
            state.pinnedAssets = state.pinnedAssets.indexOf(payload) === -1
                ? [...state.pinnedAssets, payload]
                : state.pinnedAssets;
            if (state.visibleAssetsMarkers.indexOf(payload) === -1) {
                state.visibleAssetsMarkers.push(payload);
            }
            return state;
        },
        removeFromPinnedAsset: (state, { payload }) => {
            //console.log("Unpinning asset", payload);
            state.pinnedAssets = state.pinnedAssets.indexOf(payload) !== -1
                ? state.pinnedAssets.filter(a => a !== payload)
                : state.pinnedAssets;
            return state;
        },
        unpinAllAssets: state => {
            state.pinnedAssets = [];
        },
        addToVisibleAssetsList: (state, { payload }) => {
            state.visibleAssetsList = state.visibleAssetsList.indexOf(payload) === -1
                ? [...state.visibleAssetsList, payload]
                : state.visibleAssetsList;
            return state;
        },
        removeFromVisibleAssetsList: (state, { payload }) => {
            state.visibleAssetsList = state.visibleAssetsList.indexOf(payload) !== -1
                ? state.visibleAssetsList.filter(a => a !== payload)
                : state.visibleAssetsList;
            return state;
        },
        showAllAssets: (state) => {
            state.visibleAssetsList = state.assetList;

            // Use entity adapter to filter assets with coreData
            const allVehicles = adapterSelectors.selectAll(state);
            state.visibleAssetsMarkers = state.assetList.filter(assetId => {
                const vehicle = allVehicles.find(v => v.vehicleDevice.assetId === assetId);
                return vehicle && vehicle.coreData;
            });

            // Legacy approach - commented out
            // state.visibleAssetsMarkers = state.assetList.filter(a => state.rack[`${a}`].coreData);

            state.detailView = null;
            return state;
        },
        clearVisibleAssetsMarkers: (state) => {
            state.visibleAssetsMarkers = [...state.pinnedAssets];
            if (state.detailView) {
                if (state.visibleAssetsMarkers.indexOf(state.detailView) === -1) {
                    state.visibleAssetsMarkers = [...state.visibleAssetsMarkers, state.detailView];
                }
            }
            return state;
        },
        restoreVisibleAssetsMarkers: (state) => {
            // Use entity adapter to filter assets with coreData
            const allVehicles = adapterSelectors.selectAll(state);
            state.visibleAssetsMarkers = state.visibleAssetsList.filter(assetId => {
                const vehicle = allVehicles.find(v => v.vehicleDevice.assetId === assetId);
                return vehicle && vehicle.coreData;
            });

            // Legacy approach - commented out
            // state.visibleAssetsMarkers = state.visibleAssetsList.filter(a => state.rack[`${a}`].coreData);

            return state;
        },
        showAllAssetsmarkers: (state) => {
            // Use entity adapter to filter assets with coreData
            const allVehicles = adapterSelectors.selectAll(state);
            state.visibleAssetsMarkers = state.assetList.filter(assetId => {
                const vehicle = allVehicles.find(v => v.vehicleDevice.assetId === assetId);
                return vehicle && vehicle.coreData;
            });

            // Legacy approach - commented out
            // state.visibleAssetsMarkers = state.assetList.filter(a => state.rack[`${a}`].coreData);

            state.detailView = null;
            return state;
        },
        setVisibleAssetsMarkers: (state, { payload }) => {
            const list = Array.isArray(payload) ? payload : [];
            const uniqList = Array.from(new Set(list));
            const allVehicles = adapterSelectors.selectAll(state);
            state.visibleAssetsMarkers = uniqList.filter(assetId => {
                const vehicle = allVehicles.find(v => v.vehicleDevice.assetId === assetId);
                return vehicle && vehicle.coreData;
            });
            return state;
        },
        addToVisbleAssetsMarkers: (state, { payload }) => {
            //console.log("Showing marker", payload);
            if (state.visibleAssetsMarkers.indexOf(payload) === -1) {
                state.visibleAssetsMarkers.push(payload);
            }
            return state;
        },
        removeFromVisibleAssetsMarkers: (state, { payload }) => {
            //console.log("hiding marker", payload);
            state.visibleAssetsMarkers = state.visibleAssetsMarkers.filter(a => a !== payload);
        },
        searchAssetName: (state, { payload }) => {
            //console.log("Search", payload);
            let list = [];
            if (payload === "") {
                list = state.assetList;
            } else {
                list = [...state.pinnedAssets];
                // Use entity adapter to search by registration number
                const allVehicles = adapterSelectors.selectAll(state);

                state.assetList.forEach(assetId => {
                    const vehicle = allVehicles.find(v => v.vehicleDevice.assetId === assetId);
                    if (vehicle && vehicle.vehicleDevice) {
                        if (vehicle.vehicleDevice.regn.search(payload) !== -1) {
                            if (list.indexOf(assetId) === -1) {
                                list.push(vehicle.vehicleDevice.assetId);
                            }
                        }
                    }
                });

                // Legacy approach - commented out
                /*
                state.assetList.forEach(assetId => {
                    if (state.rack[`${assetId}`].vehicleDevice) {
                        if (state.rack[`${assetId}`].vehicleDevice.regn.search(payload) !== -1) {
                            if (list.indexOf(assetId) === -1) {
                                list.push(state.rack[`${assetId}`].vehicleDevice.assetId);
                            }
                        }
                    }
                });
                */
            }

            state.visibleAssetsList = list;
            state.visibleAssetsMarkers = state.visibleAssetsMarkers.filter(a => list.indexOf(a) !== -1);

            // Add missing assets with coreData to markers
            const allVehicles = adapterSelectors.selectAll(state);
            list.forEach(assetId => {
                if (state.visibleAssetsMarkers.indexOf(assetId) === -1) {
                    const vehicle = allVehicles.find(v => v.vehicleDevice.assetId === assetId);
                    if (vehicle && vehicle.coreData) {
                        state.visibleAssetsMarkers.push(assetId);
                    }
                }
            });

            // Legacy approach - commented out
            /*
            list.forEach(assetId => {
                if (state.visibleAssetsMarkers.indexOf(assetId) === -1 && state.rack[`${assetId}`].coreData) {
                    state.visibleAssetsMarkers.push(assetId);
                }
            });
            */

            state.detailView = null;
            return state;
        },
        statusFilter: (state, { payload }) => {
            let list = [];
            if (payload === "" || payload === 'All') {
                state.visibleAssetsList = state.assetList;
            } else {
                list = [...state.pinnedAssets];

                // Use entity adapter to filter by status
                const allVehicles = adapterSelectors.selectAll(state);

                state.assetList.forEach(assetId => {
                    const vehicle = allVehicles.find(v => v.vehicleDevice.assetId === assetId);
                    if (vehicle && vehicle.statusMeta) {
                        if (vehicle.statusMeta.status === payload) {
                            if (list.indexOf(assetId) === -1) {
                                list.push(vehicle.vehicleDevice.assetId);
                            }
                        }
                    }
                });

                state.visibleAssetsList = list;
                state.visibleAssetsMarkers = state.visibleAssetsMarkers.filter(a => list.indexOf(a) !== -1);

                list.forEach(assetId => {
                    if (state.visibleAssetsMarkers.indexOf(assetId) === -1) {
                        state.visibleAssetsMarkers.push(assetId);
                    }
                });

                state.detailView = null;
            }
            return state;
        },
        summaryFilter: (state, { payload }) => {
            let list = [];
            if (payload === "" || payload === 'All') {
                state.visibleAssetsList = state.assetList;
            } else {
                list = [...state.pinnedAssets];

                // Use entity adapter to filter by summary status
                const allVehicles = adapterSelectors.selectAll(state);

                state.assetList.forEach(assetId => {
                    const vehicle = allVehicles.find(v => v.vehicleDevice.assetId === assetId);
                    if (vehicle && vehicle.statusMeta) {
                        if (vehicle.statusMeta.summaryStatus === payload) {
                            if (list.indexOf(assetId) === -1) {
                                list.push(vehicle.vehicleDevice.assetId);
                            }
                        }
                    }
                });

                state.visibleAssetsList = list;
                state.visibleAssetsMarkers = state.visibleAssetsMarkers.filter(a => list.indexOf(a) !== -1);

                list.forEach(assetId => {
                    if (state.visibleAssetsMarkers.indexOf(assetId) === -1) {
                        state.visibleAssetsMarkers.push(assetId);
                    }
                });

                state.detailView = null;
            }
            return state;
        },
        resetShareLink: (state, { payload }) => {
            // Find and update vehicle in the entity adapter
            const vehicle = adapterSelectors.selectById(state, payload.assetId);
            if (vehicle) {
                vehiclesAdapter.updateOne(state, {
                    id: payload.assetId,
                    changes: { shareLink: null }
                });
            // Legacy update
                // state.rack[`${payload.assetId}`].shareLink = null;
            }
            return state;
        },
        setViewType: (state, { payload }) => {
            state.viewType = payload;
        },
        unload: (state) => {
            // Reset entity adapter state
            vehiclesAdapter.removeAll(state);

            // Reset other state properties
            state.isLoading = false;
            state.loaded = false;
            state.socketLive = false;
            state.assetList = [];

            // Create empty rack for backward compatibility
            state.rack = {};

            // state.rack = {}; // Legacy state - commented out
            state.pinnedAssets = [];
            state.visibleAssetsList = [];
            state.visibleAssetsMarkers = [];
            state.detailView = null;
            state.fleetSummary = {};
            state.hasGensetVehicles = false;
            state.hasFuelVehicles = false;
        }


    },
    extraReducers: {
        [firstLoad.fulfilled]: (state, { payload }) => {
            //console.log("First Load Success Rack load start");
            const { list, rack, assetNames, vehicleEntities, hasGensetVehicles, hasFuelVehicles } = hydration(payload);

            // Update state with adapter
            vehiclesAdapter.setAll(state, vehicleEntities);

            // Calculate summaries using entity adapter
            const allVehicles = adapterSelectors.selectAll(state);
            state.fleetSummary = summaryMakerAdapter(allVehicles);
            state.hasGensetVehicles = hasGensetVehicles;
            state.hasFuelVehicles = hasFuelVehicles;

            // Maintain backward compatibility - will be removed in future
            state.assetList = list;
            state.assetNames = assetNames;

            // Create empty rack for backward compatibility if needed
            state.rack = {};

            /* Legacy rack updates - commented out
            state.rack = rack;
            state.visibleAssetsMarkers = list.filter(a => rack[`${a}`].coreData && rack[`${a}`].dataSanity);
            */

            // Use adapter-based approach for markers
            state.visibleAssetsMarkers = list.filter(assetId => {
                const vehicle = adapterSelectors.selectById(state, assetId);
                return vehicle && vehicle.coreData && vehicle.dataSanity;
            });

            state.visibleAssetsList = list;
            if (list.length < 20) {
                state.viewType = "fullList";
            } else {
                state.viewType = "summary";
            }

            state.loaded = true;
            state.isLoading = false;
            //console.log("Rack loaded done", state.rack);
            return state;
        },
        [firstLoad.rejected]: (_, { payload }) => {
            //console.log("FirstLoad rjected", payload);
        },
        [firstLoad.pending]: (state) => {
            //console.log("First load pending");
            state.isLoading = true;
            return state;
        },
        [getAddress.fulfilled]: (state, { payload }) => {
            //console.log("Address", payload);
            // Update vehicle in entity adapter
            vehiclesAdapter.updateOne(state, {
                id: payload.assetId,
                changes: { address: payload.address }
            });

            // Legacy update
            // state.rack[`${payload.assetId}`].address = payload.address;
        },
        [getKilometersOneAsset.fulfilled]: (state, { payload }) => {
            //console.log("One Asset KM Report", payload);
            // Update vehicle in entity adapter
            vehiclesAdapter.updateOne(state, {
                id: payload.assetId,
                changes: { distanceMatrix: payload.distances }
            });

            // Legacy update
            // state.rack[`${payload.assetId}`].distanceMatrix = payload.distances;
            return state;
        },
        [changeOdometer.fulfilled]: (state, { payload }) => {
            // Update vehicle in entity adapter
            const vehicle = adapterSelectors.selectById(state, payload.assetId);
            if (vehicle) {
                const updatedCoreData = {
                    ...vehicle.coreData,
                    odometer: payload.odometer
                };

                vehiclesAdapter.updateOne(state, {
                    id: payload.assetId,
                    changes: { coreData: updatedCoreData }
                });

            // Legacy update
                // state.rack[`${payload.assetId}`].coreData.odometer = payload.odometer;
            }
            return state;
        },
        [changeIcon.fulfilled]: (state, { payload }) => {
            // Update vehicle in entity adapter
            const vehicle = adapterSelectors.selectById(state, payload.assetId);
            if (vehicle) {
                const updatedVehicleDevice = {
                    ...vehicle.vehicleDevice,
                    icon: payload.app_icon
                };

                vehiclesAdapter.updateOne(state, {
                    id: payload.assetId,
                    changes: { vehicleDevice: updatedVehicleDevice }
                });

            // Legacy update
                // state.rack[`${payload.assetId}`].vehicleDevice.icon = payload.app_icon;
            }
            return state;
        },
        [shareLink.fulfilled]: (state, { payload }) => {
            //console.log("sharelink", payload);
            // Update vehicle in entity adapter
            vehiclesAdapter.updateOne(state, {
                id: payload.assetId,
                changes: { shareLink: payload.link }
            });

            // Legacy update
            // state.rack[`${payload.assetId}`].shareLink = payload.link;
            return state;
        },
    }
});
export { firstLoad, getAddress, getKilometersOneAsset, changeOdometer, changeIcon, shareLink };

// Get the entity adapter selectors
const adapterSelectors = vehiclesAdapter.getSelectors();

// Create adapter-based selectors
const selectVehiclesState = state => state.assets;
export const selectAllVehicles = createSelector(
    selectVehiclesState,
    adapterSelectors.selectAll
);
export const selectVehicleIds = createSelector(
    selectVehiclesState,
    adapterSelectors.selectIds
);
export const selectVehicleById = (state, id) =>
    adapterSelectors.selectById(state.assets, id);
export const selectVehicleByIdMemoized = createSelector(
    [selectVehiclesState, (_, id) => id],
    (state, id) => adapterSelectors.selectById(state, id)
);
export const selectVehiclesByIds = createSelector(
    [selectAllVehicles, (_, ids) => ids],
    (vehicles, ids) => vehicles.filter(v => ids.includes(v.vehicleDevice.assetId))
);
export const selectVehicleEntities = createSelector(
    selectVehiclesState,
    adapterSelectors.selectEntities
);

// Backward compatibility selectors
export const assetsSelector = state => state.assets;
export const hasGensetVehiclesSelector = state => state.assets.hasGensetVehicles;
export const hasFuelVehiclesSelector = state => state.assets.hasFuelVehicles;

/* Legacy rack selector - commented out
export const rackSelector = createSelector(
    selectAllVehicles,
    (vehicles) => {
        // Create rack-like structure from normalized entities
        return vehicles.reduce((rack, vehicle) => {
            rack[vehicle.vehicleDevice.assetId] = vehicle;
            return rack;
        }, {});
    }
);
*/

// New replacement for rackSelector - provides rack-like structure from entity adapter
export const getAllVehiclesAsRack = createSelector(
    selectAllVehicles,
    (vehicles) => {
        // Create rack-like structure from normalized entities
        return vehicles.reduce((rack, vehicle) => {
            rack[vehicle.vehicleDevice.assetId] = vehicle;
            return rack;
        }, {});
    }
);

export const assetNamesSelector = state => state.assets.assetNames;
export const selectAssetNameById = createSelector(
    [assetNamesSelector, (_, id) => id],
    (assetNames, id) => assetNames[id] || null
);
export const assetViewTypeSelector = state => state.assets.viewType;
export const assetListSelector = state => state.assets.assetList;
export const visibleAssetsListSelctor = state => state.assets.visibleAssetsList;
export const pinnedAssetsSelector = state => state.assets.pinnedAssets;
export const visibleAssetsMarkersSelector = state => state.assets.visibleAssetsMarkers;
export const detailViewSelector = state => state.assets.detailView;
export const fleetSummarySelector = state => state.assets.fleetSummary;

export const {
    assetCombineData,
    assetCoreData,
    assetSensorData,
    socketIsLive,
    socketIsDead,
    setDetailView,
    removeDetailView,
    addToPinnedAsset,
    removeFromPinnedAsset,
    unpinAllAssets,
    addToVisibleAssetsList,
    removeFromVisibleAssetsList,
    showAllAssets,
    clearVisibleAssetsMarkers,
    restoreVisibleAssetsMarkers,
    setVisibleAssetsMarkers,
    addToVisbleAssetsMarkers,
    removeFromVisibleAssetsMarkers,
    searchAssetName,
    statusFilter,
    summaryFilter,
    resetShareLink,
    setViewType,
    unload,

} = assetsSlice.actions;


function hydration(hydrationData) {
    const { assetList, rackData } = hydrationData;
    let list = [];
    let assetNames = {};
    let hasGensetVehicles = false;
    let hasFuelVehicles = false;

    // Create normalized entities for the adapter
    const vehicleEntities = [];
    //console.log("Hydrating rack with data", hydrationData);
    assetList.forEach(a => {
        if (!rackData[`${a.asset_id}`]) {
            console.log("Data Missing", a.regn, a.asset_id);
        } else {
            list.push(a.asset_id);

            const formattedSensors = sensorFormatter(rackData[a.asset_id].sensors);
            if (!hasGensetVehicles && hasSensorMatch(formattedSensors, ['generator', 'genset', 'gen'])) {
                hasGensetVehicles = true;
            }
            if (!hasFuelVehicles && hasSensorMatch(formattedSensors, ['fuel', 'diesel'])) {
                hasFuelVehicles = true;
            }

            // Create vehicle entity for adapter
            const vehicle = {
                vehicleDevice: {
                    assetId: `${a.asset_id}`,
                    imei: `${a.asset_id}`,
                    regn: `${a.regn}`.toUpperCase(),
                    icon: a.icon,
                    group: a.group,
                    showIcon: a.showIcon,
                    secured: a.secured,
                    vehicleInfo: a.vehicleInfo
                },
                coreData: rackData[a.asset_id].coreData,
                sensorData: formattedSensors,
                tail: [],
                dataSanity: rackData[a.asset_id].coreData ?
                    rackData[a.asset_id].coreData.lat &&
                        rackData[a.asset_id].coreData.lng &&
                        rackData[a.asset_id].coreData.dt_tracker ? true : false
                    : false,
                distanceMatrix: 'none',
            };

            // Add statusMeta
            vehicle.statusMeta = rackData[a.asset_id].coreData ?
                statusMeta(rackData[a.asset_id].coreData) :
                {
                    colorbg: "#ffffff",
                    colorfg: "#000000",
                    status: "No Data",
                    summaryStatus: "Malfunction",
                    colortext: "#000000",
                    hbtage: -1,
                    locage: -1,
                };

            // Add to entities array
            vehicleEntities.push(vehicle);

            // For backward compatibility - keep assetNames
            assetNames[`${a.asset_id}`] = {
                assetId: `${a.asset_id}`,
                imei: `${a.asset_id}`,
                regn: `${a.regn}`.toUpperCase(),
                icon: a.icon,
                vehicleInfo: a.vehicleInfo
            };

            /* Legacy rack building - commented out
            rack = {
                ...rack,
                [`${a.asset_id}`]: {
                    vehicleDevice: {
                        assetId: `${a.asset_id}`,
                        imei: `${a.asset_id}`,
                        regn: `${a.regn}`.toUpperCase(),
                        icon: a.icon,
                        group: a.group,
                        showIcon: a.showIcon,
                        secured: a.secured,
                        vehicleInfo: a.vehicleInfo
                    },
                    coreData: rackData[a.asset_id].coreData,
                    sensorData: sensorFormatter(rackData[a.asset_id].sensors),
                    tail: [],
                    statusMeta: rackData[a.asset_id].coreData ?
                        statusMeta(rackData[a.asset_id].coreData) :
                        {
                            colorbg: "#ffffff",
                            colorfg: "#000000",
                            status: "No Data",
                            summaryStatus: "Malfunction",
                            colortext: "#000000",
                            hbtage: -1,
                            locage: -1,
                        },
                    distanceMatrix: 'none',
                    dataSanity: rackData[a.asset_id].coreData ?
                        rackData[a.asset_id].coreData.lat && rackData[a.asset_id].coreData.lng && rackData[a.asset_id].coreData.dt_tracker ? true : false
                        : false,
                }
            };
            rack = {
                ...rack,
                [`${a.asset_id}`]: {
                    ...rack[`${a.asset_id}`],
                    genState: gensetMeta(rack[`${a.asset_id}`].sensorData, rack[`${a.asset_id}`].statusMeta),
                }
            };
            */
        }
    });

    // Create empty rack object for backward compatibility - will be removed in future
    const rack = {};

    return { list, rack, assetNames, vehicleEntities, hasGensetVehicles, hasFuelVehicles };
}

function summaryMaker(rack) {
    let smr = {};
    Object.values(rack).forEach(asset => {
        const { statusMeta: { status, summaryStatus, colortext } } = asset;
        if (status && summaryStatus) {
            if (smr[summaryStatus]) {
                if (smr[summaryStatus].status[status]) {
                    smr = {
                        ...smr,
                        [summaryStatus]: {
                            ...smr[summaryStatus],
                            count: smr[summaryStatus].count + 1,
                            status: {
                                ...smr[summaryStatus].status,
                                [status]: {
                                    ...smr[summaryStatus].status[status],
                                    count: smr[summaryStatus].status[status].count + 1,
                                }
                            }
                        }
                    }
                } else {
                    smr = {
                        ...smr,
                        [summaryStatus]: {
                            ...smr[summaryStatus],
                            count: smr[summaryStatus].count + 1,
                            status: {
                                ...smr[summaryStatus].status,
                                [status]: {
                                    ...smr[summaryStatus][status],
                                    color: colortext,
                                    count: 1,
                                },
                            }
                        }
                    }
                }
            } else {
                smr = {
                    ...smr,
                    [summaryStatus]: {
                        count: 1,
                        color: statusColors()[summaryStatus],
                        status: {
                            [status]: {
                                color: colortext,
                                count: 1,
                            },
                        }
                    }
                }
            }
        } else {
            //console.log("No status");
        }
    })
    let orderedSummary = {};
    if (smr.Moving) {
        orderedSummary = {
            ...orderedSummary,
            Moving: smr.Moving,
        }
    }
    if (smr.Stationary) {
        orderedSummary = {
            ...orderedSummary,
            Stationary: smr.Stationary,
        }
    }
    if (smr.Malfunction) {
        orderedSummary = {
            ...orderedSummary,
            Malfunction: smr.Malfunction,
        }
    }
    //console.log("Fleet Summary", orderedSummary);
    return orderedSummary;
}

function summaryMakerAdapter(vehicles) {
    let smr = {};
    vehicles.forEach(asset => {
        const { statusMeta: { status, summaryStatus, colortext } } = asset;
        if (status && summaryStatus) {
            if (smr[summaryStatus]) {
                if (smr[summaryStatus].status[status]) {
                    smr = {
                        ...smr,
                        [summaryStatus]: {
                            ...smr[summaryStatus],
                            count: smr[summaryStatus].count + 1,
                            status: {
                                ...smr[summaryStatus].status,
                                [status]: {
                                    ...smr[summaryStatus].status[status],
                                    count: smr[summaryStatus].status[status].count + 1,
                                }
                            }
                        }
                    }
                } else {
                    smr = {
                        ...smr,
                        [summaryStatus]: {
                            ...smr[summaryStatus],
                            count: smr[summaryStatus].count + 1,
                            status: {
                                ...smr[summaryStatus].status,
                                [status]: {
                                    ...smr[summaryStatus][status],
                                    color: colortext,
                                    count: 1,
                                },
                            }
                        }
                    }
                }
            } else {
                smr = {
                    ...smr,
                    [summaryStatus]: {
                        count: 1,
                        color: statusColors()[summaryStatus],
                        status: {
                            [status]: {
                                color: colortext,
                                count: 1,
                            },
                        }
                    }
                }
            }
        } else {
            //console.log("No status");
        }
    });

    let orderedSummary = {};
    if (smr.Moving) {
        orderedSummary = {
            ...orderedSummary,
            Moving: smr.Moving,
        }
    }
    if (smr.Stationary) {
        orderedSummary = {
            ...orderedSummary,
            Stationary: smr.Stationary,
        }
    }
    if (smr.Malfunction) {
        orderedSummary = {
            ...orderedSummary,
            Malfunction: smr.Malfunction,
        }
    }

    return orderedSummary;
}

/* Legacy genset summary function - commented out
const gensetSummary = (rack) => {
    const genStateCount = {
        "Moving Genset On": { count: 0, color: 'MediumSeaGreen' },
        "Moving Genset Off": { count: 0, color: 'Crimson' },
        "Stationary Genset On": { count: 0, color: 'DeepSkyBlue' },
        "Stationary Genset Off": { count: 0, color: 'grey' }
    };

    Object.values(rack).forEach(asset => {
        const { genState } = asset;
        genStateCount[genState].count++;
    });

    return genStateCount;
};
*/

// Create adapter-based summary update selector
export const updateSummaries = createSelector(
    selectAllVehicles,
    (vehicles) => {
        return {
            fleetSummary: summaryMakerAdapter(vehicles)
        };
    }
);

function statusMeta(coreData) {

    let colorbg = "#ffffff";
    let colorfg = "#000000";
    let status = "";
    let summaryStatus = "";
    let colortext = "#000000";
    let hbtage = -1;
    let locage = -1;

    const maxlocgap = 30;
    const maxhbtgap = 60;

    if (!coreData.lat || !coreData.lng || !coreData.dt_tracker) {
        return {
            colorbg: "#ffffff",
            colorfg: "#000000",
            status: "No Data",
            summaryStatus: "Malfunction",
            colortext: "#000000",
            hbtage: -1,
            locage: -1,
        };
    } else {



        hbtage = moment().diff(moment(coreData.dt_server), 'minutes');
        locage = moment().diff(moment(coreData.dt_tracker), 'minutes');

        let { engine, mpower, speed } = coreData;
        //console.log("Before Speed, engine, mpower", speed, engine, mpower);

        if (!coreData.hasOwnProperty('engine')) {
            engine = 0;
        }
        if (!coreData.hasOwnProperty('mpower')) {
            mpower = 0;
        }
        if (!coreData.hasOwnProperty('speed')) {
            speed = 0;
        }

        //console.log("AFTER Speed, engine, mpower", speed, engine, mpower);
        //console.log("Speed", speed, parseInt(speed), coreData.keys.indexOf("speed"));
        /**/


        if (mpower === "1" || mpower === 1) {
            colorbg = "#2C3E50";
            colorfg = "#ffffff";
            status = "Cut Off";
            summaryStatus = "Malfunction";
            colortext = "#17202A";

        } else {
            if (hbtage < maxhbtgap) {
                if (engine === "0" || engine === 0) {
                    if (speed < 4) {
                        colorbg = "#F1C40F";
                        colorfg = "#000000";
                        status = "Parked";
                        summaryStatus = "Stationary";
                        colortext = "#D68910";
                        if (locage > maxlocgap) {
                            colorfg = "#566573";
                        }
                    } else {
                        colorbg = "#6E2C00";
                        colorfg = "#ffffff";
                        status = "Weak Signal";
                        summaryStatus = "Malfunction";
                        colortext = "#6E2C00";
                        if (locage > maxlocgap) {
                            colorfg = "#E5E7E9";
                        }
                    }
                } else {
                    if (speed < 4) {
                        colorbg = "#7A1156";
                        colorfg = "#ffffff";
                        status = "Idling";
                        summaryStatus = "Stationary";
                        colortext = "#7A1156";
                        if (locage > maxlocgap) {
                            colorfg = "#E5E7E9";
                        }
                    } else if (speed < 30) {
                        colorbg = "#3498DB";
                        colorfg = "#ffffff";
                        status = "Slow";
                        summaryStatus = "Moving";
                        colortext = "#3498DB";
                        if (locage > maxlocgap) {
                            colorfg = "#E5E7E9";
                        }
                    } else if (speed < 70) {
                        colorbg = "#27AE60";
                        colorfg = "#ffffff";
                        status = "Cruising";
                        summaryStatus = "Moving";
                        colortext = "#27AE60";
                        if (locage > maxlocgap) {
                            colorfg = "#E5E7E9";
                        }
                    } else {
                        colorbg = "#E74C3C";
                        colorfg = "#ffffff";
                        status = "Fast";
                        summaryStatus = "Moving";
                        colortext = "#E74C3C";
                        if (locage > maxlocgap) {
                            colorfg = "#E5E7E9";
                        }
                    }
                }
            } else {
                colorbg = "#7F8C8D";
                colorfg = "#ffffff";
                status = "No Signal";
                summaryStatus = "Malfunction";
                colortext = "#424949";
            }
        }

        return {
            colorbg,
            colorfg,
            status,
            summaryStatus,
            colortext,
            hbtage,
            locage
        }
    }
}

const statusColors = () => {
    return {
        Moving: 'green',
        Stationary: 'yellow',
        Malfunction: 'grey'

    };
}

const sensorFormatter = (sensors, lastData) => {

    const sensorGroupTypes = ['Sensors', 'Security', 'Device'];
    const sensorGroups = {
        'vehicle_status': 'Sensors',
        'reefer_status': 'Sensors',
        'other_sensors': 'Sensors',
        'security_status': 'Security',
        'system_health': 'Device'
    };
    //console.log("Raw Sensors", sensors);
    let sensorData = {};
    for (let group in sensors) {
        //console.log("For group", group);
        if (group !== 'dt_tracker') {
            for (let sensor in sensors[group]) {
                //console.log("For Sensor Group:", group, sensorGroups[group], "Sensor", sensor, "sensor value", sensors[group][sensor]);
                sensorData = {
                    ...sensorData,
                    [sensorGroups[group]]: {
                        ...sensorData[sensorGroups[group]],
                        [sensor]: sensors[group][sensor],
                    },
                };
            }
        }
    }
    if (lastData) {
        sensorGroupTypes.forEach(group => {
            if (!sensorData.hasOwnProperty(group)) {
                if (lastData.hasOwnProperty(group)) {
                    sensorData[group] = lastData[group]
                }
            } else {
                for (let sensor in lastData[group]) {
                    if (!sensorData[group].hasOwnProperty(sensor)) {
                        sensorData[group][sensor] = lastData[group][sensor];
                    }
                }
            }
        });
    }

    //console.log("Formatted Sensors", sensorData);

    return sensorData;
}

const hasSensorMatch = (sensorData, keywords) => {
    if (!sensorData || !Array.isArray(keywords) || keywords.length === 0) return false;
    const groups = Object.values(sensorData);
    for (let i = 0; i < groups.length; i += 1) {
        const group = groups[i];
        if (!group || typeof group !== 'object') continue;
        const keys = Object.keys(group);
        for (let j = 0; j < keys.length; j += 1) {
            const key = String(keys[j] || '').toLowerCase();
            for (let k = 0; k < keywords.length; k += 1) {
                if (key.includes(String(keywords[k]).toLowerCase())) return true;
            }
        }
    }
    return false;
};
